import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * StatusBadge — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * Use everywhere status is shown (active, pending, overdue, paid, failed,
 * draft, expiring, info, beta, featured). Status must never be conveyed
 * by colour alone — the badge always carries text and an optional dot.
 */

export type StatusTone =
  | 'active'
  | 'paid'
  | 'pending'
  | 'expiring'
  | 'overdue'
  | 'failed'
  | 'draft'
  | 'info'
  | 'beta'
  | 'featured';

/**
 * Tint background, `-dark` foreground.
 *
 * Every tone here used to set its text in the DEFAULT semantic colour, which
 * is a FILL shade: success 3.24:1 on white, warning 3.39:1, trust 4.03:1,
 * danger 4.11:1. The badge renders at 12px semibold, so the 3:1 large-text
 * allowance does not apply and all of those fail WCAG AA — nine of ten tones,
 * across 69 files of "paid" / "overdue" / "failed" labels. `beta` was the only
 * one that passed, because ink is already a text colour.
 *
 * The `-dark` shades exist for exactly this and are measured against the 10%
 * tint each sits on. If you add a tone, pick the `-dark` shade, not the fill.
 */
const TONES: Record<StatusTone, string> = {
  active: 'bg-success/10 text-success-dark',
  paid: 'bg-success/10 text-success-dark',
  pending: 'bg-warning/10 text-warning-dark',
  expiring: 'bg-warning/10 text-warning-dark',
  overdue: 'bg-danger/10 text-danger-dark',
  failed: 'bg-danger/10 text-danger-dark',
  draft: 'bg-muted text-muted-foreground',
  info: 'bg-trust/10 text-trust-dark',
  beta: 'bg-ink/10 text-ink',
  // `featured` sat on accent/20 — a light orange — carrying warning-orange text.
  // Ink is the AA-safe foreground on the brand orange everywhere else in the system
  // (6.63:1); it should be here too.
  featured: 'bg-accent/20 text-ink',
};

interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: StatusTone;
  dot?: boolean;
  children: React.ReactNode;
}

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ tone, dot = false, className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs font-semibold uppercase tracking-wider',
        TONES[tone],
        className
      )}
      {...props}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  )
);
StatusBadge.displayName = 'StatusBadge';

export { StatusBadge };
export type { StatusBadgeProps };
